import { useState } from "react";
import type { CreditCalculationResult } from "@karat/shared";
import { formatUAH, formatUAHPerMonth } from "../utils/format";

/**
 * п.10 ТЗ: три рівні деталізації.
 * 1) головне число — щомісячний платіж;
 * 2) другий рівень — аванс/строк/залишковий платіж;
 * 3) третій рівень (за кнопкою "Детальні умови") — ставка/банк/КАСКО/комісія/інші витрати.
 */
export function CreditProgramCard({
  result,
  selected,
  onSelect,
}: {
  result: CreditCalculationResult;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="card credit-card"
      style={selected ? { border: "2px solid var(--color-primary)" } : { border: "2px solid transparent" }}
      onClick={onSelect}
      role={onSelect ? "button" : undefined}
    >
      <div className="credit-card-headline">
        <span className="credit-card-program">{result.programDisplayName}</span>
      </div>
      <div className="credit-card-monthly">{formatUAHPerMonth(result.monthlyPaymentUAH)}</div>
      <div className="credit-card-secondary">
        <span>
          Аванс <b>{formatUAH(result.advanceUAH)}</b>
        </span>
        <span>
          Строк <b>{result.termMonths} міс.</b>
        </span>
        <span>
          Залишковий <b>{formatUAH(result.residualPaymentUAH)}</b>
        </span>
      </div>

      <button
        className="btn btn-ghost btn-sm"
        style={{ alignSelf: "flex-start", padding: "6px 0" }}
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
      >
        {expanded ? "Приховати деталі" : "Детальні умови"}
      </button>

      {expanded && (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", display: "flex", flexDirection: "column", gap: 4 }}>
          <div>
            Ставка: <b style={{ color: "var(--color-text)" }}>{result.annualRatePercent}%</b> річних
          </div>
          <div>
            Банк: <b style={{ color: "var(--color-text)" }}>{result.bankName}</b>
          </div>
          <div>
            КАСКО: <b style={{ color: "var(--color-text)" }}>{formatUAH(result.oneTimeCosts.kaskoUAH)}</b>
          </div>
          <div>
            Комісія банку: <b style={{ color: "var(--color-text)" }}>{formatUAH(result.oneTimeCosts.bankCommissionUAH)}</b>
          </div>
          <div>
            Пенсійний збір: <b style={{ color: "var(--color-text)" }}>{formatUAH(result.oneTimeCosts.pensionFundFeeUAH)}</b>
          </div>
          <div>
            Держреєстр обтяжень: <b style={{ color: "var(--color-text)" }}>{formatUAH(result.oneTimeCosts.pledgeRegistryFeeUAH)}</b>
          </div>
          <div>
            ОСЦПВ (орієнтовно): <b style={{ color: "var(--color-text)" }}>{formatUAH(result.oneTimeCosts.osagoFeeUAH)}</b>
          </div>
          <div>
            Реєстрація авто (орієнтовно): <b style={{ color: "var(--color-text)" }}>{formatUAH(result.oneTimeCosts.registrationFeeUAH)}</b>
          </div>
          <div>
            Переплата за весь строк: <b style={{ color: "var(--color-text)" }}>{formatUAH(result.totalInterestUAH)}</b>
          </div>
          <div>
            Загальні витрати за весь строк: <b style={{ color: "var(--color-text)" }}>{formatUAH(result.totalCostUAH)}</b>
          </div>
        </div>
      )}
    </div>
  );
}
